import { Container, Text } from 'pixi.js';

let measureCanvas;
/** @type {CanvasRenderingContext2D | null} */
let measureCtx;

/**
 * @param {string} fontFamily
 * @param {number} fontSize
 * @param {string} fontWeight
 */
function getMeasureCtx(fontFamily, fontSize, fontWeight) {
  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas');
    measureCtx = measureCanvas.getContext('2d');
  }
  if (measureCtx) measureCtx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  return measureCtx;
}

/**
 * Curved label for circular UI (characters placed along a top arc).
 * @param {{
 *   radius?: number,
 *   fontSize?: number,
 *   fontFamily?: string,
 *   fontWeight?: string,
 *   fill?: number | string,
 *   letterSpacing?: number,
 *   yOffset?: number,
 *   arcSweep?: number,
 *   arcOffset?: number,
 * }} [initialOpts]
 */
export function createArcTextController(initialOpts = {}) {
  const root = new Container();
  root.eventMode = 'none';

  /** @type {Required<typeof initialOpts> & { radius: number, fontSize: number, fontFamily: string, fontWeight: string, fill: number | string, letterSpacing: number, yOffset: number }} */
  let opts = {
    radius: 76,
    fontSize: 10,
    fontFamily: '"Montserrat", "Arial", sans-serif',
    fontWeight: '800',
    fill: 0xffffff,
    letterSpacing: 0.8,
    yOffset: -2,
    arcSweep: undefined,
    arcOffset: 0,
    ...initialOpts,
  };
  let text = '';

  const rebuild = () => {
    root.removeChildren();
    const value = String(text ?? '');
    if (!value) return;

    const {
      radius,
      fontSize,
      fontFamily,
      fontWeight,
      fill,
      letterSpacing,
      yOffset,
      arcSweep,
      arcOffset,
    } = opts;

    const ctx = getMeasureCtx(fontFamily, fontSize, fontWeight);
    const chars = [...value];
    const charWidths = chars.map((ch) => {
      const base = ctx ? ctx.measureText(ch).width : fontSize * 0.55;
      const extra = ch === ' ' ? letterSpacing * 1.4 : letterSpacing;
      return base + extra;
    });
    const totalArcLen = Math.max(1, charWidths.reduce((sum, w) => sum + w, 0) - letterSpacing);
    const sweep = arcSweep ?? Math.min(Math.PI * 0.74, totalArcLen / radius + 0.06);

    let cumulative = 0;
    for (let i = 0; i < chars.length; i++) {
      const w = charWidths[i];
      const centerAlong = cumulative + w / 2;
      cumulative += w;
      const t = centerAlong / totalArcLen;
      const angle = -Math.PI / 2 - sweep / 2 + t * sweep + arcOffset;

      const glyph = new Text({
        text: chars[i],
        style: {
          fontFamily,
          fontSize,
          fontWeight,
          fill,
          align: 'center',
        },
      });
      glyph.anchor.set(0.5);
      glyph.x = Math.cos(angle) * radius;
      glyph.y = Math.sin(angle) * radius + yOffset;
      glyph.rotation = angle + Math.PI / 2;
      glyph.roundPixels = true;
      glyph.eventMode = 'none';
      root.addChild(glyph);
    }
  };

  return {
    container: root,
    /** @param {string} next */
    setText(next) {
      text = next;
      rebuild();
    },
    /** @param {typeof initialOpts} next */
    setLayout(next) {
      opts = { ...opts, ...next };
      rebuild();
    },
  };
}
